/**
 * Next-occurrence arithmetic for recurring tasks.
 *
 * Pure and dependency-free so it can be unit-tested without a database, and
 * imported by both the server (which spawns the next occurrence) and the
 * client (which previews it).
 *
 * Everything works on local-time calendar fields rather than raw millisecond
 * arithmetic. Adding 24h*7 to a Date is wrong across a daylight-saving change:
 * a 09:00 weekly task becomes 08:00 or 10:00. Using setDate() keeps the
 * wall-clock time the user chose.
 */

export const RECUR_FREQS = ['daily', 'weekly', 'monthly', 'weekdays'] as const;
export type RecurFreq = (typeof RECUR_FREQS)[number];

export const RECUR_FROMS = ['due', 'completion'] as const;
export type RecurFrom = (typeof RECUR_FROMS)[number];

export interface Recurrence {
  freq: RecurFreq;
  /** "every N". Ignored for `weekdays`. */
  interval: number;
  from: RecurFrom;
}

export const RECUR_LABELS: Record<RecurFreq, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  weekdays: 'Every weekday',
};

/** Human summary for the UI: "Every 2 weeks", "Daily". */
export function describeRecurrence(r: Recurrence | null): string {
  if (!r) return 'Does not repeat';
  if (r.freq === 'weekdays') return 'Every weekday';
  if (r.interval === 1) return RECUR_LABELS[r.freq];

  const unit = r.freq === 'daily' ? 'days' : r.freq === 'weekly' ? 'weeks' : 'months';
  return `Every ${r.interval} ${unit}`;
}

/** Days in a given month — the month argument is 0-indexed, as in Date. */
function daysInMonth(year: number, month: number): number {
  // Day 0 of the following month is the last day of this one.
  return new Date(year, month + 1, 0).getDate();
}

/**
 * The next occurrence after `anchor`.
 *
 * `anchor` is the previous due date when `from` is 'due', or the completion
 * time when it is 'completion'. Returns a new Date; the input is not mutated.
 *
 * The result is always strictly after `anchor`, which matters for `weekdays`
 * and for intervals that could otherwise land back on the anchor itself.
 */
export function nextOccurrence(anchor: Date, recurrence: Recurrence): Date {
  const { freq, interval } = recurrence;
  const step = Math.max(1, Math.floor(interval));
  const next = new Date(anchor.getTime());

  switch (freq) {
    case 'daily':
      next.setDate(next.getDate() + step);
      return next;

    case 'weekly':
      next.setDate(next.getDate() + 7 * step);
      return next;

    case 'weekdays': {
      // Always the next working day, regardless of interval: "every weekday"
      // with an interval of 3 is not a thing anyone means.
      do {
        next.setDate(next.getDate() + 1);
      } while (next.getDay() === 0 || next.getDay() === 6);
      return next;
    }

    case 'monthly': {
      // Month arithmetic has to be done by hand. Setting the month directly
      // on a Date overflows: 31 January + 1 month becomes 3 March, because
      // February has no 31st. Clamping to the last day of the target month is
      // what a person means by "monthly from the 31st".
      const targetMonth = next.getMonth() + step;
      const year = next.getFullYear() + Math.floor(targetMonth / 12);
      const month = ((targetMonth % 12) + 12) % 12;

      const day = Math.min(anchor.getDate(), daysInMonth(year, month));
      next.setFullYear(year, month, day);
      return next;
    }
  }
}

/**
 * The due date for the occurrence that follows a completed task.
 *
 * Returns null when the task does not recur, or when it recurs from its due
 * date but never had one — "every week from no particular day" has no answer,
 * and inventing today would quietly turn a scheduling bug into data.
 */
export function nextDueDate(
  previousDueAt: string | null,
  completedAt: Date,
  recurrence: Recurrence,
): Date | null {
  if (recurrence.from === 'completion') {
    return nextOccurrence(completedAt, recurrence);
  }

  if (!previousDueAt) return null;

  const previous = new Date(previousDueAt);
  if (Number.isNaN(previous.getTime())) return null;

  // Anchored to the due date, a task completed weeks late would otherwise
  // spawn its next occurrence already overdue — and then again, and again,
  // one per completion, until the series caught up. Skip forward to the
  // first occurrence that is still in the future.
  let next = nextOccurrence(previous, recurrence);
  let guard = 0;
  while (next.getTime() <= completedAt.getTime()) {
    next = nextOccurrence(next, recurrence);
    // A malformed interval must not spin forever.
    if ((guard += 1) > 1000) return null;
  }

  return next;
}
